import { ZeroAddress } from 'ethers'

import gasTankFeeTokens from '../../consts/gasTankFeeTokens'
import { networks } from '../../consts/networks'

/**
 * One asset the relayer actually credits a gas-tank deposit for. Normalized
 * from `GET <relayer>/gas-tank/assets`, which has two shapes in the wild: the
 * ZKX Go backend returns bare `{ chainId, address, symbol }` rows, while the
 * legacy Ambire-style relayer returns richer objects (decimals, icon, price).
 * Only `chainId` + `address` are load-bearing for deposit eligibility.
 */
export type GasTankTopUpAsset = {
  chainId: bigint
  address: string
  /**
   * The backend's advertised minimum top-up for this asset, in USD. Optional:
   * the field is `omitempty` on the backend, and an absent value means "no
   * advertised minimum" — never a hard 0. Enforcement is the wallet's job
   * BEFORE broadcasting: once the deposit reaches the collector the indexer
   * credits any value > 0, so the backend cannot refuse a small one without
   * making the user lose their funds.
   */
  minTopupUsd?: number
}

let relayerTopUpAssets: GasTankTopUpAsset[] | null = null

const normalizeEntry = (entry: any): GasTankTopUpAsset | null => {
  if (!entry || typeof entry.address !== 'string' || !entry.address.startsWith('0x')) return null

  let chainId: bigint
  try {
    chainId = BigInt(entry.chainId)
  } catch (e) {
    return null
  }
  if (chainId <= 0n) return null

  const asset: GasTankTopUpAsset = { chainId, address: entry.address.toLowerCase() }

  const minTopupUsd = Number(entry.minTopupUsd)
  if (Number.isFinite(minTopupUsd) && minTopupUsd > 0) asset.minTopupUsd = minTopupUsd

  return asset
}

/**
 * Store the relayer-reported list of deposit-creditable assets. Returns whether
 * the payload was accepted. An empty or malformed payload is rejected (the
 * previous list — or the static fallback — stays in effect) so a broken relayer
 * response can't blank out the Top Up screen for every token at once.
 */
export const setGasTankTopUpAssets = (raw: unknown): boolean => {
  if (!Array.isArray(raw)) return false

  const normalized = raw
    .map(normalizeEntry)
    .filter((entry): entry is GasTankTopUpAsset => entry !== null)
  if (!normalized.length) return false

  relayerTopUpAssets = normalized
  return true
}

/** Test-only escape hatch back to the static-list fallback. */
export const clearGasTankTopUpAssets = (): void => {
  relayerTopUpAssets = null
}

/**
 * Whether the backend credits a gas-tank deposit of this token. This gates the
 * `canTopUpGasTank` flag (libs/portfolio/helpers.ts) — offering a token the
 * relayer's indexer ignores means the deposit reaches the fee collector but is
 * never credited (see Trello #418: ETH topped up on Ethereum vanished because
 * WETH was missing from the backend whitelist).
 *
 * A native top-up is ALWAYS wrapped first (`getTransferRequestParams` ->
 * `isNativeTopUp`) and the indexer only ever sees a wrapped-token transfer, so
 * native is creditable iff the chain's wrapped token is. A `0x0` row in the
 * relayer list does NOT mean otherwise: the ZKX backend emits it for chains
 * with `native_fee_enabled` to say "native may PAY a fee" (handlers.go —
 * "native is not a holdable gas-tank balance"), and taking that as deposit
 * eligibility is what let Trello #454 offer ETH/BNB top-ups that the indexer
 * then ignored.
 *
 * Until the relayer list is fetched (or if fetching keeps failing), the static
 * consts/gasTankFeeTokens list remains the gate, same as before.
 */
export const isGasTankTopUpToken = (tokenChainId: bigint, address: string): boolean => {
  const addr = address.toLowerCase()

  if (relayerTopUpAssets) {
    const onChain = relayerTopUpAssets.filter((a) => a.chainId === tokenChainId)

    // Checked before the direct match on purpose — a listed `0x0` is a
    // fee-payment capability, never a deposit one.
    if (addr === ZeroAddress) {
      const wrappedAddr = networks.find((n) => n.chainId === tokenChainId)?.wrappedAddr
      return !!wrappedAddr && onChain.some((a) => a.address === wrappedAddr.toLowerCase())
    }

    return onChain.some((a) => a.address === addr)
  }

  const foundFeeToken = gasTankFeeTokens.find(
    (t) => t.chainId === tokenChainId && t.address.toLowerCase() === addr
  )

  return !!foundFeeToken && !foundFeeToken?.disableGasTankDeposit
}

/**
 * The advertised minimum top-up for this asset, in USD, or null when there is
 * none to enforce (field absent, or the relayer list has not been fetched yet).
 *
 * Native mirrors `isGasTankTopUpToken`: a native top-up is wrapped before it is
 * sent, so the wrapped token's minimum is what the indexer will actually see.
 * A native `0x0` row that carries its own value still wins — the backend emits
 * the field on it too (see the `minTopupUsd` handoff).
 */
export const getGasTankMinTopUpUsd = (tokenChainId: bigint, address: string): number | null => {
  if (!relayerTopUpAssets) return null

  const addr = address.toLowerCase()
  const onChain = relayerTopUpAssets.filter((a) => a.chainId === tokenChainId)

  const candidates = onChain.filter((a) => a.address === addr)

  if (addr === ZeroAddress) {
    const wrappedAddr = networks.find((n) => n.chainId === tokenChainId)?.wrappedAddr?.toLowerCase()
    if (wrappedAddr) candidates.push(...onChain.filter((a) => a.address === wrappedAddr))
  }

  const min = candidates.find((a) => typeof a.minTopupUsd === 'number')?.minTopupUsd

  return typeof min === 'number' ? min : null
}
